import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { GridSortModel, GridFilterModel, GridRowModesModel } from "@mui/x-data-grid-premium";
import { FirewallRequestState } from "./firewallRequestTypes";
import {
  GetFirewallRequestBySubstationGenerateNiApiArg,
  GetFirewallRequestBySubstationGenerateSiApiArg,
  firewallRequestApi,
} from "../../app/apislices/firewallRequestApi";
import { deviceApi } from "../../app/apislices/deviceApi";
import { measuringPointApi } from "../../app/apislices/measuringPointApi";
import { activeMeasuringPointApi } from "../../app/apislices/activeMeasuringPointApi";
import { TagDescription } from "@reduxjs/toolkit/query";

const initialState: FirewallRequestState = {
  sortModel: [
    { field: "__row_group_by_columns_group__", sort: "asc" },
    { field: "switchgear", sort: "asc" },
  ],
  filterModel: { items: [] },
  rowModesModel: {},
};

const firewallRequestSlice = createSlice({
  name: "firewallRequestSlice",
  initialState,
  reducers: {
    setSortModel: (state, action: PayloadAction<GridSortModel>) => {
      state.sortModel = action.payload;
    },
    setFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.filterModel = action.payload;
    },
    setRowModesModel: (state, action: PayloadAction<GridRowModesModel>) => {
      state.rowModesModel = action.payload;
    },
  },
});

export const { setSortModel, setFilterModel, setRowModesModel } = firewallRequestSlice.actions;

export default firewallRequestSlice.reducer;

firewallRequestApi.enhanceEndpoints({
  endpoints: {
    getFirewallRequestBySubstationGenerateSi: {
      transformResponse: (response: Blob, meta: unknown, args: GetFirewallRequestBySubstationGenerateSiApiArg) => {
        downloadFile(response, "antrag_si.xlsx");
        return new Promise(() => "");
      },
      query: (queryArg) => ({
        url: `/FirewallRequest/${queryArg}/generateSI`,
        method: "GET",
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
      invalidatesTags: [],
    },
    getFirewallRequestBySubstationGenerateNi: {
      transformResponse: (response: Blob, meta: unknown, args: GetFirewallRequestBySubstationGenerateNiApiArg) => {
        downloadFile(response, "antrag_ni.xlsx");
        return new Promise(() => "");
      },
      query: (queryArg) => ({
        url: `/FirewallRequest/${queryArg}/generateNI`,
        method: "GET",
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
      invalidatesTags: [],
    },
  },
});

deviceApi.enhanceEndpoints({
  addTagTypes: ["FirewallRequest"],
  endpoints: {
    putDeviceById(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "Device">[]),
        "FirewallRequest",
      ];
    },
    postDevice(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "Device">[]),
        "FirewallRequest",
      ];
    },
    deleteDeviceById(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "Device">[]),
        "FirewallRequest",
      ];
    },
  },
});

measuringPointApi.enhanceEndpoints({
  addTagTypes: ["FirewallRequest"],
  endpoints: {
    putMeasuringPointByName(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "MeasuringPoint">[]),
        "FirewallRequest",
      ];
    },
    deleteMeasuringPointByName(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "MeasuringPoint">[]),
        "FirewallRequest",
      ];
    },
  },
});

activeMeasuringPointApi.enhanceEndpoints({
  addTagTypes: ["FirewallRequest"],
  endpoints: {
    putActiveMeasuringPointByName(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "ActiveMeasuringPoint">[]),
        "FirewallRequest",
      ];
    },
    deleteActiveMeasuringPointByName(endpoint) {
      endpoint.invalidatesTags = [
        ...(endpoint.invalidatesTags as TagDescription<"FirewallRequest" | "ActiveMeasuringPoint">[]),
        "FirewallRequest",
      ];
    },
  },
});

const downloadFile = (result: Blob, filename: string) => {
  const hiddenElement = document.createElement("a");
  const url = window.URL || window.webkitURL;
  const blobPDF = url.createObjectURL(result);
  hiddenElement.href = blobPDF;
  hiddenElement.target = "_blank";
  hiddenElement.download = filename;
  hiddenElement.click();
  hiddenElement.remove();
  URL.revokeObjectURL(blobPDF);
  return { data: null };
};
